import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';

/**
 * Presenter Экрана 2.11.4 «Реферальная программа» — Block 11 Phase 11.D
 * (ADR-0094, ADR-0005). **Последний экран Блока 11.**
 *
 * Чистая функция props → JSX. State + side effects (fetch GET /api/referral,
 * ForwardSheet open/close, clipboard) — в Container `ReferralScreen.tsx`.
 *
 * **Layout (P-046 shell):** `<main bg-canvas><Header/><section overflow-y-auto>
 * …</section><BottomBar variant="default"/></main>`. **VideoButton НЕ дублируем** —
 * дочерний экран ЛК (P-040 только на entry; хаб 2.11.1 видео уже имеет).
 *
 * **Приватность (P-009):** показываем ТОЛЬКО счётчики (`referralsCount` /
 * `paidReferralsCount`) — никаких имён/телефонов друзей. `inviteUrl` —
 * собственная ссылка пользователя.
 *
 * **Шаринг (P-019 — кнопки вертикально):** единственная кнопка «Поделиться»
 * (`onShare`) открывает shared `ForwardSheet` («Пригласить друга»:
 * WhatsApp/Telegram/MAX/«Скопировать сообщение») в Container. Системный
 * `navigator.share` больше не используем; копирование живёт внутри sheet.
 *
 * **5 состояний (P-021):** idle/loading/success/error + пустое
 * (`referralsCount===0` → «Пока никто не присоединился»).
 *
 * **Design-system:** карточки `bg-card` (P-038); body/headings `text-ink`
 * (P-039); кнопки вертикально (P-019); токены палитры (P-045); размеры (P-044).
 */

export type AsyncState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

export interface ReferralScreenViewProps {
  state: AsyncState;
  inviteUrl: string | null;
  referralsCount: number;
  paidReferralsCount: number;
  bonusAvailable: boolean;
  // «Поделиться» → открыть ForwardSheet (Container держит open state).
  onShare: () => void;
}

export function ReferralScreenView(props: ReferralScreenViewProps) {
  const hasData = props.state === 'success' || props.state === 'empty';

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Приведи друга" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-4">
        {/* idle/loading — единый плейсхолдер (P-021) */}
        {(props.state === 'idle' || props.state === 'loading') && (
          <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4">
            <p className="text-muted text-[16px] m-0" aria-live="polite">
              Загружаем вашу ссылку…
            </p>
          </div>
        )}

        {/* error (P-021) */}
        {props.state === 'error' && (
          <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4">
            <p role="alert" className="text-error text-[15px] m-0">
              Не удалось загрузить реферальную программу. Попробуйте позже.
            </p>
          </div>
        )}

        {hasData && props.inviteUrl !== null && (
          <>
            {/* 1. Интро + персональная ссылка */}
            <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-3">
              <h2 className="text-ink text-h2 font-display m-0">Приведи друга — получи −50%</h2>
              <p className="text-ink text-[15px] m-0">
                Поделитесь персональной ссылкой. Когда друг зарегистрируется и оплатит подписку, вы
                получите скидку −50% на следующий месяц.
              </p>

              <div>
                <p className="text-muted text-[13px] font-bold uppercase tracking-wider m-0 mb-1">
                  Ваша ссылка
                </p>
                <p className="text-ink text-[15px] font-medium m-0 break-all rounded-lg bg-canvas border border-icon-bg p-3">
                  {props.inviteUrl}
                </p>
              </div>

              {/* Шаринг — единая кнопка (P-019, вертикально). Открывает ForwardSheet. */}
              <button
                type="button"
                onClick={props.onShare}
                className={[
                  'w-full min-h-tap rounded-xl bg-cta border-b-2 border-cta-shadow',
                  'text-ink text-[16px] font-bold px-4 py-3',
                  'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
                ].join(' ')}
              >
                Поделиться
              </button>
            </div>

            {/* 2. Статус рефералов — ТОЛЬКО счётчики (P-009) */}
            <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-2">
              <p className="text-muted text-[13px] font-bold uppercase tracking-wider m-0">
                Ваши приглашения
              </p>
              {props.state === 'empty' ? (
                <p className="text-muted text-[16px] m-0 text-center">
                  Пока никто не присоединился
                </p>
              ) : (
                <p className="text-ink text-[16px] m-0">
                  Вы пригласили <span className="font-bold">{props.referralsCount}</span> друзей, из
                  них <span className="font-bold">{props.paidReferralsCount}</span> оплатили
                </p>
              )}
            </div>

            {/* 3. Тизер промо + (если доступен) подсказка применить на подписке */}
            <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-1">
              <p className="text-ink text-[15px] font-medium m-0">−50% за оплатившего друга</p>
              {props.bonusAvailable ? (
                <p className="text-[#0D7A2E] text-[15px] font-medium m-0">
                  Промокод доступен — примените на экране подписки.
                </p>
              ) : (
                <p className="text-muted text-[14px] m-0">
                  Скидка появится, когда приглашённый друг оплатит подписку.
                </p>
              )}
            </div>
          </>
        )}
      </section>

      <BottomBar variant="default" />
    </main>
  );
}
